import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { 
  LayoutDashboard, 
  ListTodo, 
  FileText, 
  ScrollText, 
  Settings,
  Zap,
  Users,
  Share2,
  LogOut,
  Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/queue", icon: ListTodo, label: "Queue" },
  { to: "/content", icon: FileText, label: "Content" },
  { to: "/admin/leads", icon: Users, label: "Leads" },
  { to: "/admin/distribution", icon: Share2, label: "Distribution" },
  { to: "/logs", icon: ScrollText, label: "Logs" },
  { to: "/settings", icon: Settings, label: "Settings" },
];

export default function Layout() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/session`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.authenticated) {
          setUser(response.data.user);
          setChecking(false);
        } else {
          localStorage.removeItem('auth_token');
          navigate('/login');
        }
      } catch (error) {
        localStorage.removeItem('auth_token');
        navigate('/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    navigate('/login');
  };

  if (checking) {
    return (
      <div className="flex h-screen bg-[#09090b] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[#09090b]" data-testid="main-layout">
      {/* Sidebar */}
      <aside className="w-60 border-r border-zinc-800 flex flex-col" data-testid="sidebar">
        {/* Logo */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <Zap className="h-6 w-6 text-white" />
            <div>
              <h1 className="font-mono font-medium text-white text-sm">SEO Autopilot</h1>
              <p className="text-xs text-zinc-500">Jigsaw Conferences</p>
            </div>
          </div>
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 p-2" data-testid="nav-menu">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive
                    ? "bg-zinc-800 text-white"
                    : "text-zinc-400 hover:text-white hover:bg-zinc-800/50"
                )
              }
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        
        {/* User & Logout */}
        <div className="p-4 border-t border-zinc-800">
          {user && (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white font-medium">{user.username}</p>
                <p className="text-xs text-zinc-500 capitalize">{user.role}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-zinc-400 hover:text-white"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
          <p className="text-xs text-zinc-600 font-mono mt-2">v1.2.2</p>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
